// src/components/admin/TenantIsolationTest.jsx
import React, { useState } from 'react';
import { db } from '../../config/firebase';
import { collection, addDoc, getDocs, doc, setDoc, deleteDoc, writeBatch } from 'firebase/firestore';
import { FiPlay, FiTrash2, FiCheckCircle, FiXCircle, FiLoader } from 'react-icons/fi';

/**
 * Browser-based tenant isolation testing component
 * Tests multi-tenant data separation with proper authentication
 */
const TenantIsolationTest = () => {
  const [testResults, setTestResults] = useState([]);
  const [isRunning, setIsRunning] = useState(false);
  const [testData, setTestData] = useState({
    tenant1: 'test-clinic-1',
    tenant2: 'test-clinic-2'
  });

  const logResult = (test, status, message, details = null) => {
    const result = {
      id: Date.now(),
      test,
      status,
      message,
      details,
      timestamp: new Date().toISOString()
    };
    setTestResults(prev => [...prev, result]);
    return result;
  };

  const clearResults = () => {
    setTestResults([]);
  };

  const cleanupTestData = async () => {
    try {
      logResult('Cleanup', 'info', 'Starting test data cleanup...');
      
      const batch = writeBatch(db);
      
      // Delete tenant documents
      batch.delete(doc(db, 'tenants', testData.tenant1));
      batch.delete(doc(db, 'tenants', testData.tenant2));
      
      await batch.commit();
      
      logResult('Cleanup', 'success', 'Test data cleaned up successfully');
    } catch (error) {
      logResult('Cleanup', 'error', `Cleanup failed: ${error.message}`);
    }
  };

  const runTenantIsolationTest = async () => {
    setIsRunning(true);
    setTestResults([]);
    
    try {
      logResult('Start', 'info', 'Starting comprehensive tenant isolation test...');
      
      // Phase 1: Create tenant documents
      logResult('Phase 1', 'info', 'Creating tenant documents...');
      
      await setDoc(doc(db, 'tenants', testData.tenant1), {
        id: testData.tenant1,
        name: 'Test Clinic 1',
        adminEmail: 'admin1@test.com',
        status: 'active',
        createdAt: new Date()
      });
      
      await setDoc(doc(db, 'tenants', testData.tenant2), {
        id: testData.tenant2,
        name: 'Test Clinic 2',
        adminEmail: 'admin2@test.com',
        status: 'active',
        createdAt: new Date()
      });
      
      logResult('Phase 1', 'success', 'Tenant documents created successfully');
      
      // Phase 2: Create isolated client data
      logResult('Phase 2', 'info', 'Creating isolated client data...');
      
      // Add client to tenant 1
      const client1Ref = await addDoc(collection(db, 'tenants', testData.tenant1, 'clients'), {
        name: 'John Doe - Clinic 1',
        email: 'john@clinic1.com',
        phone: '555-0001',
        tenantId: testData.tenant1,
        createdAt: new Date()
      });
      
      // Add client to tenant 2
      const client2Ref = await addDoc(collection(db, 'tenants', testData.tenant2, 'clients'), {
        name: 'Jane Smith - Clinic 2',
        email: 'jane@clinic2.com',
        phone: '555-0002',
        tenantId: testData.tenant2,
        createdAt: new Date()
      });
      
      logResult('Phase 2', 'success', 'Isolated client data created successfully');
      
      // Phase 3: Create appointments for each tenant
      logResult('Phase 3', 'info', 'Creating tenant-specific appointments...');
      
      await addDoc(collection(db, 'tenants', testData.tenant1, 'appointments'), {
        clientId: client1Ref.id,
        clientName: 'John Doe - Clinic 1',
        date: '2025-08-18',
        time: '10:00',
        type: 'Consultation',
        tenantId: testData.tenant1,
        createdAt: new Date()
      });
      
      await addDoc(collection(db, 'tenants', testData.tenant2, 'appointments'), {
        clientId: client2Ref.id,
        clientName: 'Jane Smith - Clinic 2',
        date: '2025-08-18',
        time: '14:00',
        type: 'Treatment',
        tenantId: testData.tenant2,
        createdAt: new Date()
      });
      
      logResult('Phase 3', 'success', 'Tenant-specific appointments created');
      
      // Phase 4: Verify data isolation
      logResult('Phase 4', 'info', 'Verifying data isolation...');
      
      // Get clients from each tenant
      const tenant1Clients = await getDocs(collection(db, 'tenants', testData.tenant1, 'clients'));
      const tenant2Clients = await getDocs(collection(db, 'tenants', testData.tenant2, 'clients'));
      
      const tenant1ClientCount = tenant1Clients.size;
      const tenant2ClientCount = tenant2Clients.size;
      
      // Get appointments from each tenant
      const tenant1Appointments = await getDocs(collection(db, 'tenants', testData.tenant1, 'appointments'));
      const tenant2Appointments = await getDocs(collection(db, 'tenants', testData.tenant2, 'appointments'));
      
      const tenant1AppointmentCount = tenant1Appointments.size;
      const tenant2AppointmentCount = tenant2Appointments.size;
      
      // Verify isolation
      const clientIsolationWorking = tenant1ClientCount === 1 && tenant2ClientCount === 1;
      const appointmentIsolationWorking = tenant1AppointmentCount === 1 && tenant2AppointmentCount === 1;
      const overallSuccess = clientIsolationWorking && appointmentIsolationWorking;
      
      logResult('Phase 4', overallSuccess ? 'success' : 'error', 
        `Data isolation ${overallSuccess ? 'WORKING' : 'FAILED'}`, {
          tenant1: { clients: tenant1ClientCount, appointments: tenant1AppointmentCount },
          tenant2: { clients: tenant2ClientCount, appointments: tenant2AppointmentCount }
        });
      
      // Phase 5: Cross-tenant access test
      logResult('Phase 5', 'info', 'Testing cross-tenant access prevention...');
      
      // Try to access tenant 2 data from tenant 1 context (should be isolated)
      let crossTenantBlocked = true;
      try {
        // This should only return tenant 1 data due to Firestore rules
        const crossTenantTest = await getDocs(collection(db, 'tenants', testData.tenant2, 'clients'));
        // If we can access it, isolation might not be working as expected
        if (crossTenantTest.size > 0) {
          crossTenantBlocked = false;
        }
      } catch (error) {
        // Permission denied is expected and good for isolation
        crossTenantBlocked = true;
      }
      
      logResult('Phase 5', crossTenantBlocked ? 'success' : 'warning',
        `Cross-tenant access ${crossTenantBlocked ? 'properly blocked' : 'needs review'}`);
      
      // Final results
      logResult('Complete', overallSuccess ? 'success' : 'error',
        `Tenant isolation test ${overallSuccess ? 'PASSED' : 'FAILED'}`, {
          clientIsolation: clientIsolationWorking,
          appointmentIsolation: appointmentIsolationWorking,
          crossTenantBlocked: crossTenantBlocked,
          summary: `${tenant1ClientCount + tenant2ClientCount} clients, ${tenant1AppointmentCount + tenant2AppointmentCount} appointments across 2 tenants`
        });
      
    } catch (error) {
      logResult('Error', 'error', `Test failed: ${error.message}`, { error: error.code });
    } finally {
      setIsRunning(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success': return <FiCheckCircle className="text-green-500" />;
      case 'error': return <FiXCircle className="text-red-500" />;
      case 'warning': return <FiXCircle className="text-yellow-500" />;
      default: return <FiLoader className="text-blue-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success': return 'text-green-700 bg-green-50 border-green-200';
      case 'error': return 'text-red-700 bg-red-50 border-red-200';
      case 'warning': return 'text-yellow-700 bg-yellow-50 border-yellow-200';
      default: return 'text-blue-700 bg-blue-50 border-blue-200';
    }
  };

  return (
    <div className="p-8 space-y-8">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Tenant Isolation Testing</h2>
        <p className="text-gray-600 mb-6">
          Test multi-tenant data isolation to ensure complete separation between clinic data.
        </p>
        
        <div className="flex gap-4 mb-6">
          <button
            onClick={runTenantIsolationTest}
            disabled={isRunning}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isRunning ? <FiLoader className="animate-spin" /> : <FiPlay />}
            {isRunning ? 'Running Tests...' : 'Run Isolation Test'}
          </button>
          
          <button
            onClick={clearResults}
            disabled={isRunning}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Clear Results
          </button>
          
          <button
            onClick={cleanupTestData}
            disabled={isRunning}
            className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50"
          >
            <FiTrash2 />
            Cleanup Test Data
          </button>
        </div>
        
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Tenant 1 ID</label>
            <input
              type="text"
              value={testData.tenant1}
              onChange={(e) => setTestData(prev => ({ ...prev, tenant1: e.target.value }))}
              disabled={isRunning}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Tenant 2 ID</label>
            <input
              type="text"
              value={testData.tenant2}
              onChange={(e) => setTestData(prev => ({ ...prev, tenant2: e.target.value }))}
              disabled={isRunning}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      </div>
      
      {testResults.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Test Results</h3>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {testResults.map((result) => (
              <div
                key={result.id}
                className={`p-4 rounded-lg border ${getStatusColor(result.status)}`}
              >
                <div className="flex items-start gap-3">
                  {getStatusIcon(result.status)}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium">{result.test}</span>
                      <span className="text-xs text-gray-500">
                        {new Date(result.timestamp).toLocaleTimeString()}
                      </span>
                    </div>
                    <p className="text-sm">{result.message}</p>
                    {result.details && (
                      <pre className="text-xs mt-2 p-2 bg-gray-100 rounded overflow-x-auto">
                        {JSON.stringify(result.details, null, 2)}
                      </pre>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TenantIsolationTest;
